import type { SupabaseClient } from '@supabase/supabase-js';
import { sendVarioMessage } from '../vario-iframe.js';
import { ensureOttoStyles, OTTO_ICON, ottoAppHead, ottoEscapeHtml } from './otto-ui.js';
import { httpErrorText } from '../lib/session-expiry.js';

interface InstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  handshake: { appToken: string; appIdentifier?: string; offlineToken?: string };
  supabaseUrl: string;
}

interface StatusRow { connected: boolean; status: string | null; installation_id: string | null; }

export async function renderInstall(ctx: InstallContext) {
  ensureOttoStyles();
  // Auto-Grow: iframe = Inhaltshöhe, Vario scrollt außen. Ohne diesen Marker deckelt die
  // bridge-§16-Kette die gemeldete Höhe auf den Viewport → die Karte wird unten abgeschnitten
  // und die Foot-Buttons („Abbrechen"/„Mit OTTO verbinden") liegen außerhalb des iframes.
  document.documentElement.classList.add('otto-autogrow');
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="otto-install">
      ${ottoAppHead('Marktplatz · Installation', 'App-Installation')}

      <div class="stage">
        <div class="card" id="install-card">
          <div class="card-top">
            <img class="otto-badge" src="/otto-market.png" alt="OTTO Market">
            <div class="titles">
              <h2>Otto-App installieren</h2>
              <div class="sub">Marktplatz-Anbindung für OTTO Market</div>
            </div>
            <span class="env-tag" id="env-tag"><span class="d"></span>Verbinden</span>
          </div>

          <div class="card-body">
            <p class="intro">Verbinde dein <b>OTTO-Market-Konto</b> mit Vario. Du wirst zu OTTO weitergeleitet, bestätigst dort den Zugriff und kehrst danach hierher zurück. <b>Es sind keine API-Zugangsdaten einzutippen.</b></p>

            <div class="sec-lab">OTTO Market OAuth</div>

            <div id="connect-state"><span style="display:inline-flex;align-items:center;gap:8px;font-size:12.5px;color:var(--muted);">${OTTO_ICON.info}Klicke „Mit OTTO verbinden", um den Autorisierungsprozess zu starten.</span></div>
          </div>

          <div class="card-foot">
            <span class="foot-status" id="foot-status">${OTTO_ICON.info}Noch nicht verbunden</span>
            <div class="foot-actions">
              <button class="btn btn-ghost" id="cancel-btn">${OTTO_ICON.x}Abbrechen</button>
              <button class="btn btn-primary" id="connect-btn">${OTTO_ICON.check}Mit OTTO verbinden</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const card = ctx.contentEl.querySelector<HTMLDivElement>('#install-card')!;
  const envTag = ctx.contentEl.querySelector<HTMLSpanElement>('#env-tag')!;
  const footStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#foot-status')!;
  const connectBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#connect-btn')!;
  const cancelBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#cancel-btn')!;
  const base = ctx.supabaseUrl.replace(/\/$/, '');
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  function setFoot(k: 'info' | 'ready' | 'error', h: string) {
    footStatus.className = k === 'info' ? 'foot-status' : `foot-status ${k}`;
    footStatus.innerHTML = h;
  }

  cancelBtn.addEventListener('click', () => {
    if (pollTimer) clearInterval(pollTimer);
    // Vario bietet KEIN app-seitiges Abbrechen-Signal (bestätigt vom Vario-Support 2026-07-27):
    // `installationComplete: false` schließt den Dialog nicht. `true` blendet lediglich Varios
    // eigenen Schließen-Button ein und ist der empfohlene Weg, dem Anwender einen Ausgang zu geben.
    // Intern als Abbruch behandelt (kein otto-oauth-start abgeschlossen → keine Verbindung).
    showCancelled(card);
    sendVarioMessage({ installationComplete: true });
  });

  async function pollStatus(): Promise<boolean> {
    const { data, error } = await ctx.supabase.rpc('otto_connection_status');
    if (error) return false;
    const row = (Array.isArray(data) ? data[0] : data) as StatusRow | undefined;
    return !!row?.connected;
  }

  connectBtn.addEventListener('click', async () => {
    connectBtn.disabled = true;
    connectBtn.innerHTML = `<span class="spin"></span>Öffne OTTO …`;

    try {
      const res = await fetch(`${base}/functions/v1/otto-oauth-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: '{}',
      });
      const p = await res.json().catch(() => ({})) as { ok?: boolean; authUrl?: string; error?: { message: string } };

      connectBtn.disabled = false;
      connectBtn.innerHTML = `${OTTO_ICON.check}Mit OTTO verbinden`;

      if (!res.ok || !p.ok || !p.authUrl) {
        setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml(p.error?.message ?? httpErrorText(res.status))}`);
        return;
      }

      window.open(p.authUrl, '_blank', 'noopener');
      setFoot('info', `${OTTO_ICON.info}Bestätige den Zugriff im OTTO-Fenster …`);
      envTag.className = 'env-tag';
      envTag.innerHTML = `<span class="d"></span>Warte auf Bestätigung …`;

      if (pollTimer) clearInterval(pollTimer);
      let ticks = 0;
      pollTimer = setInterval(async () => {
        ticks++;
        const connected = await pollStatus();
        if (connected) {
          if (pollTimer) clearInterval(pollTimer);
          showDone(card);
          sendVarioMessage({ installationComplete: true });
        } else if (ticks > 60) {
          if (pollTimer) clearInterval(pollTimer);
          setFoot('error', `${OTTO_ICON.info}Zeitüberschreitung — bitte erneut versuchen.`);
          connectBtn.disabled = false;
          connectBtn.innerHTML = `${OTTO_ICON.check}Mit OTTO verbinden`;
        }
      }, 3000);
    } catch (e) {
      connectBtn.disabled = false;
      connectBtn.innerHTML = `${OTTO_ICON.check}Mit OTTO verbinden`;
      setFoot('error', `${OTTO_ICON.info}Netzwerkfehler: ${ottoEscapeHtml((e as Error).message)}`);
    }
  });
}

function showDone(card: HTMLElement) {
  card.innerHTML = `
    <div class="done-card">
      <div class="done-ic">${OTTO_ICON.checkBold}</div>
      <h2>OTTO-App installiert</h2>
      <p>Die Anbindung an <b>OTTO Market</b> ist aktiv. Dein OTTO-Konto ist verbunden.</p>
      <div class="mono">Storefront: OTTO Market · DE</div>
    </div>`;
}

/**
 * Abbruch-Karte. Es gibt kein App-Signal, das das Vario-Modal schließt; `installationComplete:
 * true` (im cancel-Handler gesendet) blendet Varios eigenen Schließen-Button ein. Diese Karte
 * macht klar, dass abgebrochen wurde (keine Verbindung) und der Dialog darüber beendet werden kann.
 */
function showCancelled(card: HTMLElement) {
  card.innerHTML = `
    <div class="done-card">
      <div class="done-ic neutral">${OTTO_ICON.x}</div>
      <h2>Installation abgebrochen</h2>
      <p>Es wurde keine Verbindung angelegt. Du kannst den Dialog jetzt über den Schließen-Button beenden.</p>
    </div>`;
}
